import type { ReactElement } from 'react';
import type { ConnectedAgent } from '../api/types';
import { RevokeError, useConnectedAgents, useRevokeConnection } from '../api/account-queries';
import type { SupabaseAuth } from '../auth/supabase';
import { ErrorBanner } from '../components/ErrorBanner';
import { Badge } from '../ui/Badge';
import { Banner } from '../ui/Banner';
import { Button, ButtonLink } from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { Stack } from '../ui/Stack';
import { StatusState } from '../ui/StatusState';
import { Table, type TableColumn } from '../ui/Table';
import { useDocumentTitle } from '../useDocumentTitle';

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

const nameOf = (a: ConnectedAgent): string => a.clientName ?? 'Unnamed agent';

/** The signed-in person's connected agents (MCP OAuth clients), with an ordered revoke. No workspace shell. */
export function ConnectedAgentsRoute({ auth }: { auth: SupabaseAuth | null }): ReactElement {
  useDocumentTitle('Connected agents');
  const agents = useConnectedAgents(true);
  const revoke = useRevokeConnection(auth);
  const revokeError = revoke.error instanceof RevokeError ? revoke.error : null;

  function retry(): void {
    if (!revoke.variables) return;
    revoke.mutate(revoke.variables);
  }

  const columns: TableColumn<ConnectedAgent>[] = [
    { id: 'name', label: 'Agent', isRowHeader: true, render: nameOf },
    { id: 'connected', label: 'Connected', render: (a) => formatDate(a.connectedAt) },
    { id: 'lastUsed', label: 'Last used', render: (a) => formatDate(a.lastUsedAt) },
    {
      id: 'workspaces',
      label: 'Recent workspaces',
      render: (a) => (a.recentWorkspaces.length ? a.recentWorkspaces.map((w) => w.name).join(', ') : 'None in the last 30 days')
    },
    {
      id: 'status',
      label: 'Status',
      render: (a) => <Badge tone={a.active ? 'success' : 'neutral'}>{a.active ? 'Active' : 'Revoked'}</Badge>
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (a) =>
        a.active ? (
          <Button size="sm" variant="quiet" aria-label={`Revoke ${nameOf(a)}`} isDisabled={revoke.isPending} onPress={() => revoke.mutate(a)}>
            Revoke
          </Button>
        ) : null
    }
  ];

  return (
    <main className="p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Connected agents"
        actions={
          <ButtonLink href="/" variant="secondary">
            Back to reports
          </ButtonLink>
        }
      />
      <Stack gap={4}>
        {revokeError?.step === 'grant' ? (
          <Banner tone="danger" title="Could not revoke access. Nothing changed; the agent still has access." />
        ) : null}
        {revokeError?.step === 'record' ? (
          <Banner
            tone="warn"
            title="Access was revoked, but ReportPad could not record it. The agent's current access token keeps working for up to an hour."
            actions={
              <Button size="sm" isDisabled={revoke.isPending} onPress={retry}>
                Retry
              </Button>
            }
          />
        ) : null}
        {revoke.error && !revokeError ? <ErrorBanner error={revoke.error} /> : null}

        {agents.data && agents.data.length > 0 ? (
          <Table label="Connected agents" columns={columns} rows={agents.data} />
        ) : agents.data ? (
          <StatusState kind="empty" title="No agents connected yet." />
        ) : null}

        <ErrorBanner error={agents.error} />
      </Stack>
    </main>
  );
}
